<template>
  <div>
    <LoadingOverlay :showLoadingOverlay="loading" />
    <NoRecordsFound v-show="!loading && !tableData" />
    <div
      v-show="tableData"
      :class="$style.tableListContainer"
    >
      <ul
        v-for="(infoKeysByColumn, colIndex) in [infoKeysLeft, infoKeysRight]"
        :key="colIndex"
        :class="$style.tableList"
      >
        <li
          v-for="(infoKey, index) in infoKeysByColumn"
          :key="index"
          :class="$style.infoListItem"
        >
          <span>{{ formatInfoTitle(infoKey) }}</span>
          <span>{{ getValue(infoKey) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapActions } from 'vuex';
import Constants, { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay.vue';
import NoRecordsFound from 'client/components/Tables/NoRecordsFound.vue';
import SystemInsightsMetadata from 'client/systems/systemInsights/SystemInsightsMetadata';
import Util from 'client/util/Util';

const { metricGroupMetadata } = SystemInsightsMetadata;
const systemInsightsModule = StoreModuleMap[ItemTypeConsts.systemInsights];

export default {
  name: 'InsightsSingleItemTable',

  components: {
    LoadingOverlay,
    NoRecordsFound,
  },

  props: {
    metricGroup: {
      type: String,
      default: '',
    },
    systemId: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    loading: false,
    tableData: null,
  }),

  computed: {
    columnOneLength() {
      return Math.ceil(this.infoKeys.length / 2);
    },

    infoKeys() {
      if (isEmpty(this.tableData)) return [];
      return Object.keys(this.tableData);
    },

    infoKeysLeft() {
      return this.infoKeys.slice(0, this.columnOneLength);
    },

    infoKeysRight() {
      return this.infoKeys.slice(this.columnOneLength);
    },
  },

  watch: {
    // Need to fetch data when metric group changes
    metricGroup() {
      this.refreshData();
    },
  },

  created() {
    this.refreshData();
  },

  methods: {
    ...mapActions(systemInsightsModule, [
      'fetchInsightsData',
    ]),

    formatInfoTitle(infoKey) {
      return infoKey
        .split('_')
        .map((word) => {
          if (word === 'cpu') return 'CPU';
          if (word === 'uuid') return 'UUID';
          return word[0].toUpperCase() + word.substr(1);
        })
        .join(' ');
    },

    getValue(infoKey) {
      let value = this.tableData[infoKey];
      if (typeof value === 'undefined') return Constants.dataNotAvailable;
      if (value === '') return Constants.dataNotAvailable;
      if (value === '-1') return Constants.dataNotAvailable;

      // Convert bytes to GB
      if (infoKey === 'physical_memory') {
        const { number, unit } = Util.getTextToDisplayBytes(
          value || 0, true,
        );
        return `${number} ${unit}`;
      }

      // Format value if metadata denotes a format type for that row
      const metadata = metricGroupMetadata[this.metricGroup];
      if (metadata.dataFormat && metadata.dataFormat[infoKey]) {
        value = metadata.dataFormat[infoKey](value);
      }

      return value;
    },

    refreshData() {
      this.loading = true;

      this.fetchInsightsData({
        id: this.systemId,
        metricGroup: this.metricGroup,
        params: {},
      })
        .then((data) => {
          if (!data || isEmpty(data)) {
            this.tableData = null;
            return;
          }

          [this.tableData] = data;
        })
        .catch(() => {
          this.tableData = null;
        })
        .finally(() => {
          this.loading = false;
          this.$emit('loaded');
        });
    },
  },
};
</script>

<style module>
@import '../css/systemInsights.css';

@value (max-width-medium) from '~css/breakpoints.css';

.infoListItem {
  display: block;
}

.infoListItem > span {
  display: block;
}

.infoListItem > span:first-child {
  margin-right: 0;
}

.infoListItem > span:last-child {
  text-align: left;
}

.tableListContainer {
  display: flex;
  justify-content: space-between;
}

.tableList {
  flex: 1;
  margin: 0 !important;
  padding: 0;
}

.tableList:first-child {
  margin-right: 50px !important;
}

@media (max-width: max-width-medium) {
  .infoListItem {
    display: flex;
  }

  .infoListItem:last-child {
    margin-bottom: 0;
  }

  .infoListItem > span {
    display: inline-block;
  }

  .infoListItem > span:first-child {
    margin-right: 15px;
  }

  .infoListItem > span:last-child {
    text-align: right;
  }

  .tableListContainer {
    display: block;
  }

  .tableList {
    width: 100%;
  }

  .tableList:first-child {
    margin-bottom: 0;
    margin-right: 0;
  }
}
</style>
